// src/pages/InstitutoSer/InstitutoSer.tsx
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Typography, Grid, Box, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IMAGE_BASE_PATH } from "../../config/paths";

// ✅ Hooks existentes
import { useSeo } from "../../components/seo/useSeo";
import { useJsonLd } from "../../components/seo/useJsonLd";

const visuallyHiddenSx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

/** ✅ Defina aqui a largura X (texto / vídeo / imagem terão a MESMA) */
const CONTENT_MAX_W = 560;

/** ✅ Gap entre colunas no desktop (colado no centro) */
const CENTER_GAP_MD = 4;

type InnerAlign = "toCenterFromLeft" | "toCenterFromRight" | "center";

/**
 * ✅ Wrapper que mantém maxWidth X, centraliza,
 * e (no desktop) encosta no CENTRO do zigzag.
 */
function ConstrainedInner({
  maxW,
  align,
  isSmallScreen,
  children,
}: {
  maxW: number;
  align: InnerAlign;
  isSmallScreen: boolean;
  children: React.ReactNode;
}) {
  // No mobile: sempre centralizado
  const justify =
    isSmallScreen || align === "center"
      ? "center"
      : align === "toCenterFromLeft"
        ? "flex-end" // coluna da esquerda -> encosta no centro
        : "flex-start"; // coluna da direita -> encosta no centro

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: justify,
        width: "100%",
        minWidth: 0, // ✅ importantíssimo p/ não estourar no flex/grid
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: isSmallScreen ? "100%" : `${maxW}px`,
          minWidth: 0, // ✅ idem
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

const InstitutoSer = () => {
  const { t, i18n } = useTranslation();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // =========================
  // SEO: title/desc/canonical/jsonld
  // =========================
  const rawLang = (i18n.resolvedLanguage || i18n.language || "pt").toLowerCase();
  const langPrefix = rawLang.split("-")[0];
  const locale = langPrefix === "en" ? "en_US" : langPrefix === "es" ? "es_ES" : "pt_BR";

  const canonical = useMemo(() => {
    const origin = window.location.origin;
    return new URL("/instituto-ser", origin).toString();
  }, []);

  const ogImage = useMemo(() => {
    return new URL(
      `${IMAGE_BASE_PATH}projetosocial/InstitutoSerBanner.avif`,
      window.location.origin
    ).toString();
  }, []);

  const seoTitle = useMemo(() => `${t("InstitutoSer.seoTitle")}`, [t]);
  const seoDescription = useMemo(() => t("InstitutoSer.seoDescription"), [t]);

  useSeo({
    title: seoTitle,
    description: seoDescription,
    canonical,
    image: ogImage,
    type: "article",
    noindex: false,
    locale,
  });

  const jsonLd = useMemo(() => {
    const pageUrl = canonical;

    const breadcrumb = {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: new URL("/", window.location.origin).toString() },
        {
          "@type": "ListItem",
          position: 2,
          name: t("InstitutoSer.breadcrumbSocialProjects"),
          item: new URL("/projetos-sociais", window.location.origin).toString(),
        },
        { "@type": "ListItem", position: 3, name: t("InstitutoSer.breadcrumbCurrent"), item: pageUrl },
      ],
    };

    const webPage = {
      "@type": "WebPage",
      name: t("InstitutoSer.title"),
      url: pageUrl,
      inLanguage: langPrefix,
      description: seoDescription,
      primaryImageOfPage: { "@type": "ImageObject", url: ogImage },
    };

    const videoObject = {
      "@type": "VideoObject",
      name: t("InstitutoSer.videoObjectName"),
      description: t("InstitutoSer.videoObjectDescription"),
      thumbnailUrl: [ogImage],
      uploadDate: "2026-01-01",
      contentUrl:
        "https://firebasestorage.googleapis.com/v0/b/corelatam-6d704.appspot.com/o/InstitutoSer.mp4?alt=media&token=66efa7d2-a0bf-4cb6-8603-68e02d7124b2",
      embedUrl: pageUrl,
    };

    return { "@context": "https://schema.org", "@graph": [webPage, breadcrumb, videoObject] };
  }, [canonical, langPrefix, ogImage, seoDescription, t]);

  useJsonLd("instituto-ser", jsonLd);

  // =========================
  // Styles (mantidos)
  // =========================
  const textoDescritivoStyle = useMemo(
    () => ({
      fontFamily: "Helvetica,Arial,Verdana,sans-serif",
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: "0.02em",
      fontSize: "1rem",
      color: "black",
      textAlign: isSmallScreen ? "center" : "left",
      marginTop: 1.5,
      minWidth: 0,
      overflowWrap: "anywhere", // ✅ evita “estouro” por palavras/strings longas
    }),
    [isSmallScreen]
  );

  const textoTituloStyle = useMemo(
    () => ({
      fontFamily: "Oswald, sans-serif",
      fontWeight: 700,
      fontSize: { xs: "2.5rem", sm: "2.5rem", md: "1.8rem", lg: "2.5rem", xl: "2.5rem" },
      letterSpacing: "-0.05em",
      textTransform: "uppercase",
      textAlign: isSmallScreen ? "center" : "left",
      minWidth: 0,
      overflowWrap: "anywhere",
    }),
    [isSmallScreen]
  );

  const textoSubTituloStyle = useMemo(
    () => ({
      fontFamily: "Oswald, sans-serif",
      fontWeight: 600,
      fontSize: { xs: "1.8rem", sm: "1.8rem", md: "1.3rem", lg: "1.8rem", xl: "1.8rem" },
      letterSpacing: "-0.03em",
      textTransform: "uppercase",
      textAlign: isSmallScreen ? "center" : "left",
      minWidth: 0,
      overflowWrap: "anywhere",
    }),
    [isSmallScreen]
  );

  // =========================
  // Video Player (SEO/A11y)
  // =========================
  const VideoPlayer = useCallback(() => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
      if (videoRef.current) videoRef.current.currentTime = 0.5;
    }, []);

    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <video
          ref={videoRef}
          width="100%"
          height="auto"
          controls
          preload="metadata"
          playsInline
          poster={`${IMAGE_BASE_PATH}projetosocial/InstitutoSerBanner.avif`}
          style={{ display: "block", width: "100%", height: "auto" }}
          aria-label={t("InstitutoSer.videoAriaLabel")}
        >
          <source
            src="https://firebasestorage.googleapis.com/v0/b/corelatam-6d704.appspot.com/o/InstitutoSer.mp4?alt=media&token=66efa7d2-a0bf-4cb6-8603-68e02d7124b2"
            type="video/mp4"
          />
          {t("InstitutoSer.videoFallback")}
        </video>
      </Box>
    );
  }, [t]);

  // =========================
  // Texto entrevista
  // =========================
  const textoBoxEntrevista = useMemo(() => {
    return (
      <>
        <Typography component="h2" variant="h4" sx={textoTituloStyle} color="black">
          {t("InstitutoSer.festivalH2")}
        </Typography>

        <Typography component="h3" variant="h5" sx={textoSubTituloStyle} color="black">
          {t("InstitutoSer.festivalH3")}
        </Typography>

        <Typography component="p" sx={{ ...textoDescritivoStyle, lineHeight: 1.7, marginTop: 3 }}>
          {t("InstitutoSer.festivalP1Before")}{" "}
          <Box component="strong" sx={{ color: "#006400", fontWeight: "bold" }}>
            {t("InstitutoSer.breadcrumbCurrent")}
          </Box>
          , {t("InstitutoSer.festivalP1After")}
        </Typography>

        <Typography component="p" sx={{ ...textoDescritivoStyle, lineHeight: 1.7, marginTop: 3 }}>
          {t("InstitutoSer.festivalP2")}
        </Typography>

        <Typography component="p" sx={{ ...textoDescritivoStyle, lineHeight: 1.7, marginTop: 3 }}>
          {t("InstitutoSer.festivalP3Before")}{" "}
          <Box component="strong" sx={{ color: "black", fontWeight: "bold" }}>
            CORE
          </Box>{" "}
          {t("InstitutoSer.festivalP3Middle")}{" "}
          <Box component="strong" sx={{ color: "#006400", fontWeight: "bold" }}>
            {t("InstitutoSer.breadcrumbCurrent")}
          </Box>
          , {t("InstitutoSer.festivalP3After")}
        </Typography>
      </>
    );
  }, [t, textoDescritivoStyle, textoSubTituloStyle, textoTituloStyle]);

  // IDs para aria (SEO/A11y)
  const h1Id = "instituto-ser-title";
  const introId = "instituto-ser-intro";

  return (
    <Grid
      component="main"
      container
      spacing={4}
      justifyContent="center"
      aria-labelledby={h1Id}
      sx={{
        backgroundColor: "white",
        width: "100%",
        overflowX: "hidden", // ✅ corta overflow de spacing/neg margins
      }}
    >
      {/* ===== HERO ===== */}
      <Grid size={12} height={"80vh"} sx={{ minWidth: 0 }}>
        <Box
          component="section"
          aria-label={t("InstitutoSer.bannerAria")}
          sx={{
            position: "relative",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          {isMobile ? (
            <img
              src={`${IMAGE_BASE_PATH}projetosocial/InstitutoSerBanner.avif`}
              alt={t("InstitutoSer.bannerAlt")}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              loading="eager"
              fetchPriority="high"
            />
          ) : (
            <video
              src="https://firebasestorage.googleapis.com/v0/b/corelatam-6d704.appspot.com/o/TerceiroParaisoForum.mp4?alt=media&token=5e7728ae-9f83-4a0b-acd5-82bd88e7012a"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              aria-label={t("InstitutoSer.openingVideoAria")}
            />
          )}

          <Box sx={visuallyHiddenSx}>
            <Typography component="h1" id={h1Id}>
              {t("InstitutoSer.title")}
            </Typography>
            <Typography component="p" id={introId}>
              {seoDescription}
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* ===== TÍTULO VISUAL (H1 já está no hidden) ===== */}
      <Grid size={12} component="header" aria-describedby={introId} sx={{ minWidth: 0, overflowX: "hidden" }}>
        <Typography
          component="div"
          variant="h2"
          fontSize={{ xs: 45, sm: 65, md: 85, lg: 100, xl: 100 }}
          sx={{
            color: "black",
            textAlign: "center",
            fontFamily: "PFDinTextPro-Thin, sans-serif",
            fontWeight: 100,
            px: { xs: 2, sm: 3 }, // ✅ padding aqui é seguro (não no container com spacing)
          }}
        >
          {t("InstitutoSer.title")}
        </Typography>
      </Grid>

      {/* ===== CONTEÚDO (zig-zag mantido + mobile mídia abaixo do texto) ===== */}
      <Grid
        container
        columnSpacing={{ xs: 4, md: CENTER_GAP_MD }}
        rowSpacing={{ xs: 4, md: 4 }}
        justifyContent="center"
        component="section"
        aria-label={t("InstitutoSer.contentAria")}
        sx={{
          alignItems: "flex-start",
          width: "100%",
          overflowX: "hidden", // ✅ evita scroll lateral no mobile
          // ✅ NÃO use px grande aqui em conjunto com spacing; se precisar, use px nos conteúdos
          pb: 2,
        }}
      >
        {/* BLOCO 1 */}
        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }} sx={{ minWidth: 0 }}>
          <ConstrainedInner maxW={CONTENT_MAX_W} align="toCenterFromRight" isSmallScreen={isSmallScreen}>
            <Box sx={{ color: "#fff", textTransform: "none", px: { xs: 2, sm: 0 }, minWidth: 0 }}>
              <Typography component="h3" variant="h5" color="black" sx={textoTituloStyle}>
                {t("InstitutoSer.aboutH3")}
              </Typography>

              <Typography component="p" color="black" sx={textoDescritivoStyle}>
                <Box component="strong" sx={{ color: "#006400", fontWeight: "bold" }}>
                  {t("InstitutoSer.aboutH2")}
                </Box>{" "}
                {t("InstitutoSer.aboutIntro")}
              </Typography>

              <Typography component="p" color="black" sx={textoDescritivoStyle}>
                <Box component="strong" sx={{ color: "#00BFFF", fontWeight: "bold" }}>
                  {t("InstitutoSer.novosVentosTitle")}:
                </Box>{" "}
                {t("InstitutoSer.novosVentosText")}
              </Typography>

              <Typography component="p" color="black" sx={textoDescritivoStyle}>
                <Box component="strong" sx={{ color: "#20B2AA", fontWeight: "bold" }}>
                  {t("InstitutoSer.academiaTitle")}:
                </Box>{" "}
                {t("InstitutoSer.academiaText")}
              </Typography>

              <Typography component="p" color="black" sx={textoDescritivoStyle}>
                <Box component="strong" sx={{ color: "#FFA500", fontWeight: "bold" }}>
                  {t("InstitutoSer.coursesTitle")}:
                </Box>{" "}
                {t("InstitutoSer.coursesText")}
              </Typography>

              <Typography component="p" color="black" sx={textoDescritivoStyle}>
                <Box component="strong" sx={{ color: "#228B22", fontWeight: "bold" }}>
                  {t("InstitutoSer.bestPracticesTitle")}:
                </Box>{" "}
                {t("InstitutoSer.bestPracticesText")}
              </Typography>

              <Typography component="p" color="black" sx={textoDescritivoStyle}>
                <Box component="strong" sx={{ color: "#8B4513", fontWeight: "bold" }}>
                  {t("InstitutoSer.historyTitle")}:
                </Box>{" "}
                {t("InstitutoSer.historyText")}
              </Typography>
            </Box>
          </ConstrainedInner>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }} sx={{ minWidth: 0 }}>
          <ConstrainedInner maxW={CONTENT_MAX_W} align="toCenterFromLeft" isSmallScreen={isSmallScreen}>
            <Box sx={{ px: { xs: 2, sm: 0 }, minWidth: 0 }}>{VideoPlayer()}</Box>
          </ConstrainedInner>
        </Grid>

        {/* BLOCO 2 */}
        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 3, md: 3 }} sx={{ minWidth: 0 }}>
          <ConstrainedInner maxW={CONTENT_MAX_W} align="toCenterFromLeft" isSmallScreen={isSmallScreen}>
            <Box sx={{ px: { xs: 2, sm: 0 }, minWidth: 0 }}>{textoBoxEntrevista}</Box>
          </ConstrainedInner>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} order={{ xs: 4, md: 4 }} sx={{ minWidth: 0 }}>
          <ConstrainedInner maxW={CONTENT_MAX_W} align="toCenterFromRight" isSmallScreen={isSmallScreen}>
            <Box sx={{ px: { xs: 2, sm: 0 }, minWidth: 0 }}>
              <Box sx={{ position: "relative", overflow: "hidden", width: "100%" }}>
                <img
                  src={`${IMAGE_BASE_PATH}projetosocial/InstitutoSerEntrevista.avif`}
                  alt={t("InstitutoSer.interviewAlt")}
                  style={{
                    objectFit: "cover",
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                  loading="lazy"
                  decoding="async"
                />
              </Box>
            </Box>
          </ConstrainedInner>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InstitutoSer;
